import deepmerge from 'deepmerge';
import loadFile from './load-file';

export type LoadFilesAllProps = {
  [moduleName: string]: {
    [componentName: string]: () => void;
  };
};

export default async (
  { files, env }: any,
  cb?: ((...args: any) => any) | null,
  log?: (...args: any) => void
): Promise<LoadFilesAllProps | null> => {
  //* make sure we have files, if not, return null
  if (!files || files?.length < 1) {
    if (log) log('no files found');

    return null;
  }

  //* if we have files, loop through them, and load them
  //* when load files, we need to block until all have finished loading
  // files is the following type:
  // [ { moduleName: '', files: {}[], opts:{} } ]
  // we need to do two loops, the first one to get the modulename
  // and the second one (this.loadModule) to load the files for that module
  const loadedModules = await files?.reduce(
    async (modulePromise, { moduleName, files: componentFiles }) => {
      return modulePromise.then(async result => {
        // this returns { moduleName, files[], opts }
        const loadedModule = await componentFiles?.reduce(
          async (componentPromise, componentFile) => {
            return componentPromise.then(async moduleResult => {
              // this returns { component, core, [env] }
              const componentName = componentFile?.component;
              const { file, manifest } =
                componentFile[env || 'default'] || componentFile.default || {};
              // check to make sure we have a file to load
              if (!file) return Promise.resolve(moduleResult);

              // load the actual file/manifest
              const loadedFile = await loadFile({
                file,
                manifest,
                moduleName
              }).catch(err => {
                console.error(`failed to load ${file} from ${moduleName}`, err);

                return null;
              });

              if (loadedFile) {
                const componentMappedToModule = []
                  .concat(componentName)
                  .reduce((acc, comp) => {
                    if (loadedFile[comp])
                      return deepmerge(acc, {
                        [moduleName]: {
                          [comp]: loadedFile[comp]
                        }
                      });

                    return acc;
                  }, {});

                if (cb) cb(componentMappedToModule);

                return Promise.resolve(
                  deepmerge(moduleResult, componentMappedToModule)
                );
              }

              return Promise.resolve(moduleResult);
            });
          },
          Promise.resolve({})
        );

        return Promise.resolve(deepmerge(result, loadedModule));
      });
    },
    Promise.resolve({})
  );

  // object that is being returned is:
  // {
  //   'LibraryName': {
  //     'componentName': 'function'
  //   }
  // }

  return loadedModules;
};
